/* eslint-disable no-console */
import { useSoketi } from '#compositions/useSoketi'
import { onUnmounted, ref, watch } from 'vue'
import Notification from '~/services/notificationService'
import type { SoketiEvent } from '~/compositions/useSoketi'
import type {
  AppointmentsCheckedNotification,
  ExportDatesChangedNotification,
  GETAppointmentWithContainer,
  GETCapacitySet,
  GETContainer,
  GETExport,
  GETTruck,
  GETUserInfo,
  GlobalNotification,
  ImportStatusCheckedNotification,
  OrganizationUpdated,
  Notification as SoketiNotification,
} from '~/services/apiClient'
import {
  EventName,
  NotificationsApi,
  NotificationType,
  WebsocketChannel,
} from '~/services/apiClient'
import { useAppointmentWithContainerInfoStore } from '~/stores/appointmentsWithContainerInfo'
import { useCapacitiesStore } from '~/stores/capacities'
import { usedBasicContainersStore } from '~/stores/basicContainers'
import { useContainerStore } from '~/stores/containers'
import { useActiveVesselSchedulesStore } from '~/stores/activeVesselSchedules'
import { useExportsStore } from '~/stores/exports'
import { useGlobalNotificationsStore } from '~/stores/globalNotifications'
import { useMutationRequestsStore } from '~/stores/mutationRequests'
import { useTrucksStore } from '~/stores/trucks'
import { useUserStore } from '~/stores/user'
import type { FeedNotificationAction } from '~/stores/notificationFeed'

export function useNotificationsForLoggedInUser() {
  const soketi = useSoketi()
  // Stores
  const userStore = useUserStore()
  const capacitiesStore = useCapacitiesStore()
  const trucksStore = useTrucksStore()
  const globalNotificationsStore = useGlobalNotificationsStore()
  const basicContainersStore = usedBasicContainersStore()
  const containerStore = useContainerStore()
  const exportsStore = useExportsStore()
  const activeVesselSchedulesStore = useActiveVesselSchedulesStore()
  const appointmentsWithContainerInfoStore =
    useAppointmentWithContainerInfoStore()
  const mutationRequestsStore = useMutationRequestsStore()

  // Refs
  const connected = ref(false)

  // Setup
  const seenMessages = new Set<string>()
  setInterval(
    // clear seen messages every hour
    () => {
      seenMessages.clear()
    },
    1000 * 60 * 60
  )
  watch(
    () => [userStore.loggedIn, userStore.isTerminalOrg],
    ([loggedIn, isTerminalOrg]) => {
      if (loggedIn && !isTerminalOrg) {
        fetchActiveGlobalNotifications()
        connect()
      }
    },
    { immediate: true }
  )

  // Functions
  function fetchActiveGlobalNotifications() {
    const notificationsApi = new NotificationsApi()
    notificationsApi
      .getActiveGlobalNotificationsNotificationsGlobalGet()
      .then((response) => {
        response.data.forEach((notification) => {
          const globalNotification = notification.object as GlobalNotification
          globalNotificationsStore.update(globalNotification)
        })
      })
  }

  function connect() {
    if (!userStore.loggedIn) return
    userStore.loadInfoIfNeeded().then(() => {
      if (!userStore.isTerminalOrg) {
        soketi.bind(
          WebsocketChannel.PrivateCacheGlobalNotifications,
          EventName.GlobalNotification,
          handleSoketiMessage
        )
      }
      if (!userStore.orgNotificationsChannel) {
        console.error('No org notifications channel')
        return
      }
      console.log(
        `Binding to org notifications channel ${
          userStore.orgNotificationsChannel
        }`
      )
      soketi.bind(
        userStore.orgNotificationsChannel,
        EventName.OrganizationNotification,
        handleSoketiMessage
      )

      if (!userStore.userNotificationsChannel) {
        console.error('No user notifications channel')
        return
      }
      console.log(
        `Binding to user notifications channel ${
          userStore.userNotificationsChannel
        }`
      )
      soketi.bind(
        userStore.userNotificationsChannel,
        EventName.UserNotification,
        handleSoketiMessage
      )
    })
  }

  function disconnect() {
    soketi.unbind(
      WebsocketChannel.PrivateCacheGlobalNotifications,
      EventName.GlobalNotification,
      handleSoketiMessage
    )
    // NOTE: unbinding from org and user notifications is handled in userStore
    // should we unbind from all channels here?
  }
  onUnmounted(disconnect)

  /**
   * The button a notification carries, for the types that go somewhere.
   * A date-change alert opens that vessel's change history on the Schedule
   * board, the one export surface customers can reach (the List is
   * internal-only). The port filter is added by the router guard.
   */
  function actionFor(
    message: SoketiNotification
  ): FeedNotificationAction | undefined {
    if (message.event_type !== NotificationType.ExportDatesChanged) return
    const { schedule_id } = message.object as ExportDatesChangedNotification
    return {
      label: 'View vessel history',
      to: {
        name: 'exports',
        query: { view: 'schedule', selected_schedule: schedule_id },
      },
    }
  }

  function handleSoketiMessage(event: SoketiEvent) {
    if (!event.message_id) {
      // All messages should have a message_id I believe
      console.error('No message_id in event', event)
      return
    }
    if (seenMessages.has(event.message_id)) {
      console.log('Ignoring duplicate message for ', event.message_id)
      return
    }
    seenMessages.add(event.message_id)
    if (!('notification' in event)) {
      console.error('No notification in event', event)
      return
    }
    const message = event.notification
    if (message.title) {
      Notification.show({
        title: message.title,
        message: message.message,
        type: message.level,
        duration: 15000,
        action: actionFor(message),
      })
    }
    switch (message.event_type) {
      case NotificationType.ContainerUpdated:
        containerStore.updateContainer(message.object as GETContainer)
        basicContainersStore.updateFromGETContainer(
          message.object as GETContainer
        )
        appointmentsWithContainerInfoStore.updateFromContainer(
          message.object as GETContainer
        )
        break
      case NotificationType.AppointmentUpdated:
        appointmentsWithContainerInfoStore.updateFromAppointment(
          message.object as GETAppointmentWithContainer
        )
        break
      case NotificationType.ImportStatusesChecked:
        containerStore.updateImportStatusLastObservedTimes(
          message.object as ImportStatusCheckedNotification
        )
        break
      case NotificationType.PierPassImportStatusesChecked:
        containerStore.updatePierPassLastObservedTimes(
          message.object as ImportStatusCheckedNotification
        )
        break
      case NotificationType.LineImportStatusesChecked:
        containerStore.updateLineStatusLastObservedTimes(
          message.object as ImportStatusCheckedNotification
        )
        break
      case NotificationType.AppointmentsChecked:
        containerStore.updateAppointmentLastObservedTimes(
          message.object as AppointmentsCheckedNotification
        )
        break
      case NotificationType.CapacitySetUpdated:
        capacitiesStore.setCapacitySetsFromAPI([
          message.object as GETCapacitySet,
        ])
        break
      case NotificationType.GlobalNotification: {
        const globalNotification = message.object as GlobalNotification
        globalNotificationsStore.update(globalNotification)
        break
      }
      case NotificationType.OrganizationUpdated: {
        userStore.updateOrg(
          (message.object as OrganizationUpdated).organization
        )
        break
      }
      case NotificationType.TrucksUpdated: {
        trucksStore.addTrucksToStore([message.object as GETTruck])
        break
      }
      case NotificationType.UserUpdated: {
        const userInfo = message.object as GETUserInfo
        userStore.updateUserAndSaveToStorage(userInfo)
        break
      }
      case NotificationType.ExportDatesChanged:
        // Alert only: the BE title and message already say what moved. The
        // button is attached with the toast above.
        break
      case NotificationType.ExportUpdated:
        // Fired when a watched export booking's statuses/appointments change
        // (BE publishes one GETExport per event, appointments included). Merge
        // it in so the Exports list reflects new statuses without a manual
        // reload.
        exportsStore.mergeExports([message.object as GETExport], {
          appointmentsIncluded: true,
        })
        // A booking added, archived or closed changes which vessels are "mine".
        activeVesselSchedulesStore.reloadIfLoaded()
        break
      // A reschedule reports its outcome on its own event, not under
      // `BookingRequestUpdated` — only a cancel-and-rebook reschedule comes in
      // under that one, via its booking request. Both reschedule events must be
      // routed here or a rejected/failed reschedule never reaches the UI.
      case NotificationType.BookingRequestUpdated:
      case NotificationType.RescheduleRequestUpdated:
      case NotificationType.EmptyRescheduleRequestUpdated:
        mutationRequestsStore.handleMutationRequestUpdatedEvent(message)
        break
      case NotificationType.BookingVerification:
        mutationRequestsStore.handleAppointmentVerificationEvent(message)
        break
      default:
        console.warn('Unhandled notification type', message.event_type)
    }
  }
  return { connected, handleSoketiMessage }
}
